import { useState } from "react";
import { CornerUpLeft } from "lucide-react";

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Where a verification tab ends when there is nothing more to do in it.
 *
 * KYC and the mandate run in a tab opened from the journey; the journey tab stays open and shows
 * the next step. cpcore's selfie relay sends a tenant here instead of DigiLocker whenever KYC is
 * already complete — on production (28 Sep 2026) the relay was opened again the second the
 * mandate finished and used to forward to DigiLocker, which looked like DigiLocker opening by
 * itself.
 *
 * The relay also lands here after a failure (unknown link, no DigiLocker link), so the page never
 * says the tenant is done: it only points back to the journey tab, which knows the real state.
 */
export default function VerificationDoneScreen() {
  const [closeBlocked, setCloseBlocked] = useState(false);
  // Tabs share storage, so this shows whenever we know the journey. It is the way back for a
  // tenant whose journey tab is gone (closed, or the browser opened this flow in the same tab).
  const identifier = readStorage("user_identifier");
  const partner = readStorage("partnerIdentifier");
  const journeyUrl = identifier
    ? `/b/${identifier}?partner=${partner ?? ""}&step=onboarding`
    : null;

  const close = () => {
    window.close();
    // A tab the browser did not let us close is still open a moment later.
    setTimeout(() => setCloseBlocked(true), 400);
  };

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center"
      data-testid="verification-done"
    >
      <CornerUpLeft className="size-12 text-gray-700" aria-hidden="true" />
      <h1 className="text-xl font-semibold text-gray-900">
        Continue in your CirclePe tab
      </h1>
      <p className="max-w-sm text-sm text-gray-600">
        Your CirclePe tab shows your next step. You can close this tab.
      </p>
      <button
        type="button"
        onClick={close}
        className="rounded-lg bg-gray-900 px-6 py-3 text-sm font-medium text-white"
      >
        Close this tab
      </button>
      {closeBlocked && (
        <p className="max-w-sm text-xs text-gray-500">
          Your browser did not let us close it — please close this tab yourself.
        </p>
      )}
      {journeyUrl && (
        <a href={journeyUrl} className="text-sm text-gray-700 underline">
          Open my CirclePe application here instead
        </a>
      )}
    </div>
  );
}
